/* eslint-disable no-param-reassign */

import { useAuftragList } from 'api/auftrag';
import * as React from 'react';
import { AuftragPositionFilter, useAuftragPositionList } from 'api/auftragPosition';
import { useRechnungList } from 'api/rechnung';
import { RechnungPositionFilter, useRechnungPositionList } from 'api/rechnungPosition';
import { useSelector } from 'react-redux';
import { skontodefinitionenByArtSelector } from 'store/lookups/selector';
import { getBetreuerAnteil } from 'reports/auftragsuebersicht/utils';
import { useBetreuerprovisionListByNr } from 'api/betreuerprovision';
import { useBestellungList } from 'api/bestellung';
import { BestellungPositionFilter, useBestellungPositionList } from 'api/bestellungPosition';
import { defaultLineData, FilterValues, UmsatzSummarySourceData } from '../types';

export interface UmsatzSummarySourceDataProps extends FilterValues {
    enabled: boolean;
}

export const useUmsatzSummaryData = ({
    source,
    datumVon,
    datumBis,
    kNr,
    kundePlzVon,
    kundePlzBis,
    kundeUmsatzTsdVon,
    kundeUmsatzTsdBis,
    kundeBranchen,
    kundeErweiterteSuchen,
    kundeInteresseFuerProdukte,
    kundeBetreuer,
    kundeTelBetreuer,
    betreuer,
    betreuerGebiet,
    telBetreuer,
    telBetreuerGebiet,
    produktgruppen,
    hauptlieferantNr,
}: UmsatzSummarySourceDataProps): UmsatzSummarySourceData => {
    const betreuerprovisionListByNr = useBetreuerprovisionListByNr();
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);

    const recordListFilter = React.useMemo(
        () => ({
            datumVon,
            datumBis,
            kNr,
            kundePlzVon,
            kundePlzBis,
            kundeUmsatzVon: kundeUmsatzTsdVon ? kundeUmsatzTsdVon * 1000 : undefined,
            kundeUmsatzBis: kundeUmsatzTsdBis ? kundeUmsatzTsdBis * 1000 : undefined,
            kundeBranchen,
            kundeErweiterteSuchen,
            kundeInteresseFuerProdukte,
            kundeBetreuerExact: kundeBetreuer,
            kundeTelBetreuerExact: kundeTelBetreuer,
            betreuerExact: betreuer,
            telBetreuerExact: telBetreuer,
            produktgruppen,
            hauptlieferantNr,
            rahmen: false,
            withKunde: true,
        }),
        [
            betreuer,
            datumBis,
            datumVon,
            kNr,
            kundeBetreuer,
            kundeBranchen,
            kundeErweiterteSuchen,
            kundeInteresseFuerProdukte,
            kundePlzBis,
            kundePlzVon,
            kundeTelBetreuer,
            kundeUmsatzTsdBis,
            kundeUmsatzTsdVon,
            produktgruppen,
            hauptlieferantNr,
            telBetreuer,
        ],
    );

    const auftragList = useAuftragList(recordListFilter, {
        enabled: source === 'auftrag',
    });

    const auftragPositionFilter = React.useMemo<AuftragPositionFilter>(
        () => ({
            auNrList: auftragList.data?.map((au) => au.nr) || [],
            withArtikelDetail: true,
            withHauptlieferant: true,
        }),
        [auftragList],
    );

    const auftragPositionList = useAuftragPositionList(auftragPositionFilter, { enabled: !!auftragList.data });

    const rechnungList = useRechnungList(recordListFilter, {
        enabled: source === 'rechnung',
    });

    const rechnungPositionFilter = React.useMemo<RechnungPositionFilter>(
        () => ({
            rNrList: rechnungList.data?.map((r) => r.nr) || [],
            withArtikelDetail: true,
            withHauptlieferant: true,
        }),
        [rechnungList],
    );

    const rechnungPositionList = useRechnungPositionList(rechnungPositionFilter, { enabled: !!rechnungList.data });

    const bestellungList = useBestellungList(recordListFilter, {
        enabled: source === 'bestellung',
    });

    const bestellungPositionFilter = React.useMemo<BestellungPositionFilter>(
        () => ({
            bNrList: bestellungList.data?.map((b) => b.nr) || [],
            withArtikelDetail: true,
            withHauptlieferant: true,
        }),
        [bestellungList],
    );

    const bestellungPositionList = useBestellungPositionList(bestellungPositionFilter, {
        enabled: !!bestellungList.data,
    });

    return React.useMemo(() => {
        let umsatzSummarySourceData: UmsatzSummarySourceData;
        if (source === 'auftrag') {
            umsatzSummarySourceData = {
                isLoading:
                    auftragList.isLoading ||
                    auftragList.isFetching ||
                    auftragPositionList.isLoading ||
                    auftragPositionList.isFetching,
                total: { ...defaultLineData },
                recordList: (auftragList.data || [])
                    .filter((auftrag) => {
                        if (![1, 9, 10, 4, 0].includes(auftrag.status) || auftrag.rahmen) {
                            return false;
                        }

                        if (betreuer && betreuerGebiet && auftrag.kunde?.betreuer !== betreuer) {
                            return false;
                        }

                        if (telBetreuer && telBetreuerGebiet && auftrag.kunde?.telBetreuer !== telBetreuer) {
                            return false;
                        }
                        return true;
                    })
                    .map((auftrag) => ({
                        kNr: auftrag.kNr || 0,
                        nr: auftrag.nr,
                        datum: auftrag.datum,
                        betreuer: auftrag.betreuer,
                        telBetreuer: auftrag.telBetreuer,
                        sNetto: auftrag.sNetto,
                        zahlBed: auftrag.zahlBed,
                        fracht: auftrag.fracht,
                        betrUm: auftrag.betrUm,
                        roadPr: auftrag.roadPr,
                        tipp: auftrag.tipp,
                        kunde: auftrag.kunde,
                    })),
                recordPositionList: (auftragPositionList.data || []).map((auftragPosition) => ({
                    kNr: 0,
                    kName: '?',
                    recordNr: auftragPosition.auNr,
                    datum: '',
                    betreuer: 0,
                    telBetreuer: 0,
                    betrUm: false,
                    pos: auftragPosition.pos,
                    artikelGruppeNr: auftragPosition.artikelGruppeNr,
                    artikelNr: auftragPosition.artikelNr,
                    posText: auftragPosition.posText,
                    posEinh: auftragPosition.posEinh,
                    posMenge: auftragPosition.posMenge,
                    posEkPreis: auftragPosition.posEkPreis,
                    posEPreis: auftragPosition.posEPreis,
                    posNetto: auftragPosition.posNetto,
                    lArt: auftragPosition.lArt,
                    artikelDetail: auftragPosition.artikelDetail,
                    hauptlieferant: auftragPosition.hauptlieferant,
                })),
            };
        } else if (source === 'rechnung') {
            umsatzSummarySourceData = {
                isLoading:
                    rechnungList.isLoading ||
                    rechnungList.isFetching ||
                    rechnungPositionList.isLoading ||
                    rechnungPositionList.isFetching,
                total: { ...defaultLineData },
                recordList: (rechnungList.data || [])
                    .filter((rechnung) => {
                        if (![0, 1, 5, 6].includes(rechnung.status)) {
                            return false;
                        }

                        if (betreuer && betreuerGebiet && rechnung.kunde?.betreuer !== betreuer) {
                            return false;
                        }

                        if (telBetreuer && telBetreuerGebiet && rechnung.kunde?.telBetreuer !== telBetreuer) {
                            return false;
                        }
                        return true;
                    })
                    .map((rechnung) => ({
                        kNr: rechnung.kNr || 0,
                        nr: rechnung.nr,
                        datum: rechnung.datum,
                        betreuer: rechnung.betreuer,
                        telBetreuer: rechnung.telBetreuer,
                        sNetto: rechnung.sNetto,
                        zahlBed: rechnung.zahlBed,
                        fracht: rechnung.fracht,
                        // betrUm: false,
                        roadPr: rechnung.roadPr,
                        tipp: rechnung.tipp,
                        kunde: rechnung.kunde,
                    })),
                recordPositionList: (rechnungPositionList.data || []).map((rechnungPosition) => ({
                    kNr: 0,
                    kName: '?',
                    recordNr: rechnungPosition.rNr,
                    datum: '',
                    betreuer: 0,
                    telBetreuer: 0,
                    // betrUm: false,
                    pos: rechnungPosition.pos,
                    artikelGruppeNr: rechnungPosition.artikelGruppeNr,
                    artikelNr: rechnungPosition.artikelNr,
                    posText: rechnungPosition.posText,
                    posEinh: rechnungPosition.posEinh,
                    posMenge: rechnungPosition.posMenge,
                    posEkPreis: rechnungPosition.posEkPreis,
                    posEPreis: rechnungPosition.posEPreis,
                    posNetto: rechnungPosition.posNetto,
                    artikelDetail: rechnungPosition.artikelDetail,
                    hauptlieferant: rechnungPosition.hauptlieferant,
                })),
            };
        } else if (source === 'bestellung') {
            umsatzSummarySourceData = {
                isLoading:
                    bestellungList.isLoading ||
                    bestellungList.isFetching ||
                    bestellungPositionList.isLoading ||
                    bestellungPositionList.isFetching,
                total: { ...defaultLineData },
                recordList: (bestellungList.data || [])
                    .filter((bestellung) => {
                        if (![0, 1, 8, 4].includes(bestellung.status)) {
                            return false;
                        }

                        if (betreuer && betreuerGebiet && bestellung.kunde?.betreuer !== betreuer) {
                            return false;
                        }

                        if (telBetreuer && telBetreuerGebiet && bestellung.kunde?.telBetreuer !== telBetreuer) {
                            return false;
                        }
                        return true;
                    })
                    .map((bestellung) => ({
                        kNr: bestellung.kNr || 0,
                        nr: bestellung.nr,
                        datum: bestellung.datum,
                        betreuer: bestellung.betreuer,
                        telBetreuer: 0,
                        sNetto: bestellung.sNetto,
                        zahlBed: bestellung.zahlBed,
                        fracht: 0,
                        // betrUm: false,
                        roadPr: 0,
                        tipp: 0,
                        kunde: bestellung.kunde,
                    })),
                recordPositionList: (bestellungPositionList.data || []).map((bestellungPosition) => ({
                    kNr: 0,
                    kName: '?',
                    recordNr: bestellungPosition.bNr,
                    datum: '',
                    betreuer: 0,
                    telBetreuer: 0,
                    // betrUm: false,
                    pos: bestellungPosition.pos,
                    artikelGruppeNr: bestellungPosition.artikelGruppeNr,
                    artikelNr: bestellungPosition.artikelNr,
                    posText: bestellungPosition.posText,
                    posEinh: bestellungPosition.posEinh,
                    posMenge: bestellungPosition.posMenge,
                    posEkPreis: bestellungPosition.posEkPreis,
                    posEPreis: bestellungPosition.posEPreis,
                    posNetto: bestellungPosition.posNetto,
                    artikelDetail: bestellungPosition.artikelDetail,
                    hauptlieferant: bestellungPosition.hauptlieferant,
                })),
            };
        } else {
            throw new Error('unexpected source: ' + source);
        }

        umsatzSummarySourceData.recordPositionList.forEach((position) => {
            const record = umsatzSummarySourceData.recordList.find((r) => r.nr === position.recordNr);

            if (!record) {
                return;
            }

            const positionCount = umsatzSummarySourceData.recordPositionList.filter(
                (p) => p.recordNr === position.recordNr,
            ).length;

            const skontoP = skontodefinitionenByArt[record.zahlBed]?.skP1 || 0;

            const ekNetto = position.posEkPreis * position.posMenge;
            const vkNetto = position.posEPreis * position.posMenge;

            const tippProv = record.tipp === 0 ? 0 : (vkNetto / 100) * record.tipp;
            const skonto = skontoP === 0 ? 0 : (vkNetto / 100) * skontoP;
            const bMarge = vkNetto - ekNetto;
            const nMarge = bMarge - tippProv - skonto - (record.fracht + record.roadPr) / positionCount;

            const { value: bAnteil } = getBetreuerAnteil(
                betreuerprovisionListByNr,
                record.betreuer,
                record.datum,
                nMarge,
                0,
            );
            const fAnteil = nMarge - bAnteil;
            // const fracht = record.fracht + record.roadPr;

            position.kNr = record.kNr;
            position.kName = record.kunde?.name1 || '?';
            position.datum = record.datum;
            position.betreuer = record.betreuer;
            position.telBetreuer = record.telBetreuer;

            position.lineData = {
                count: 1,
                countP: 0,
                recordNrList: [],
                groupById: '',
                groupByLabel: '',
                ekNetto,
                ekNettoP: 0,
                vkNetto,
                vkNettoP: 0,
                bMarge,
                bMargeP: 0,
                // tippProv,
                // skonto,
                // fracht,
                nMarge,
                nMargeP: 0,
                bAnteil,
                fAnteil,
            };

            umsatzSummarySourceData.total.count += 1;

            umsatzSummarySourceData.total.ekNetto += ekNetto;
            umsatzSummarySourceData.total.vkNetto += vkNetto;
            umsatzSummarySourceData.total.bMarge += bMarge;
            // umsatzSummarySourceData.total.tippProv += tippProv;
            // umsatzSummarySourceData.total.skonto += skonto;
            // umsatzSummarySourceData.total.fracht += record.fracht + record.roadPr;
            umsatzSummarySourceData.total.nMarge += nMarge;
            umsatzSummarySourceData.total.bAnteil += bAnteil;
            umsatzSummarySourceData.total.fAnteil += fAnteil;
        });

        return umsatzSummarySourceData;
    }, [
        source,
        auftragList.isLoading,
        auftragList.isFetching,
        auftragList.data,
        auftragPositionList.isLoading,
        auftragPositionList.isFetching,
        auftragPositionList.data,
        betreuer,
        betreuerGebiet,
        telBetreuer,
        telBetreuerGebiet,
        rechnungList.isLoading,
        rechnungList.isFetching,
        rechnungList.data,
        rechnungPositionList.isLoading,
        rechnungPositionList.isFetching,
        rechnungPositionList.data,
        bestellungList.isLoading,
        bestellungList.isFetching,
        bestellungList.data,
        bestellungPositionList.isLoading,
        bestellungPositionList.isFetching,
        bestellungPositionList.data,
        skontodefinitionenByArt,
        betreuerprovisionListByNr,
    ]);
};
